import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';
import { Science } from '@mui/icons-material';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import type {
  AdminOrganizationResponse,
  AdminOrganizationUserResponse,
} from '@store/generated/organizations';
import type { ShadowNoteResponse } from '@store/generated/shadow-notes';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { ShadowNoteViewerDialog } from '../components/shadow-note-viewer-dialog';
import {
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  useGetV2AdminOrganizationsQuery,
} from '../store/hooks';
import { useListShadowNotesQuery } from '../store/shadowNotesApi';
import { formatDateTime, formatRelativeTime, getStatusBadgeColor } from '../utils/formatting';

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const columns = [
  {
    key: 'shadow_note_id' as keyof ShadowNoteResponse,
    label: 'ID',
    render: (value: unknown) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
        {typeof value === 'string' ? `${value.substring(0, 12)}...` : '—'}
      </Typography>
    ),
  },
  {
    key: 'template_title' as keyof ShadowNoteResponse,
    label: 'Template',
    render: (value: unknown, row: ShadowNoteResponse) => (
      <Box>
        <Typography variant="body2">
          {typeof value === 'string' ? value : 'Unknown template'}
        </Typography>
        {row.template_version != null && (
          <Typography variant="caption" color="text.secondary">
            v{row.template_version}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    key: 'status' as keyof ShadowNoteResponse,
    label: 'Status',
    render: (value: unknown) => (
      <Chip
        label={typeof value === 'string' ? value : 'unknown'}
        color={getStatusBadgeColor(typeof value === 'string' ? value : null)}
        size="small"
      />
    ),
  },
  {
    key: 'note_title' as keyof ShadowNoteResponse,
    label: 'Note Title',
    render: (value: unknown) => (
      <Typography
        variant="body2"
        sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {typeof value === 'string' ? value : '—'}
      </Typography>
    ),
  },
  {
    key: 'created_by' as keyof ShadowNoteResponse,
    label: 'Created By',
    render: (value: unknown) => (
      <Typography variant="body2">
        {typeof value === 'string' ? value.substring(0, 16) : '—'}
      </Typography>
    ),
  },
  {
    key: 'created_at' as keyof ShadowNoteResponse,
    label: 'Created',
    render: (value: unknown) => (
      <Box>
        <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDateTime(value as string)}
        </Typography>
      </Box>
    ),
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ShadowNotesList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- State ----
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<AdminOrganizationResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminOrganizationUserResponse | null>(null);
  const [viewingShadowNoteId, setViewingShadowNoteId] = useState<string | null>(null);

  const orgIdParam = searchParams.get('organizationId');
  const userIdParam = searchParams.get('userId');

  // ---- Org search (debounced, hits API) ----
  const debouncedOrgSearch = useDebounce(orgSearch, 300);
  const { data: orgsData, isLoading: orgsLoading } = useGetV2AdminOrganizationsQuery({
    search: debouncedOrgSearch || undefined,
    perPage: 50,
  });
  const organizations = useMemo(() => orgsData?.items ?? [], [orgsData]);

  // ---- Org detail (for users list when org is selected) ----
  const effectiveOrgId = selectedOrg?.organization_id ?? orgIdParam ?? '';
  const { data: orgDetail, isLoading: orgDetailLoading } =
    useGetV2AdminOrganizationsByOrganizationIdQuery(
      { organizationId: effectiveOrgId },
      { skip: !effectiveOrgId },
    );
  const orgUsers = useMemo(
    () => (orgDetail?.users ?? []).filter(u => u.status === 'active'),
    [orgDetail],
  );

  // ---- Shadow notes query ----
  const { data, isLoading, error, refetch } = useListShadowNotesQuery(
    {
      organizationId: effectiveOrgId,
      ...(selectedUser ? { userId: selectedUser.user_id } : {}),
      limit: 50,
    },
    { skip: !effectiveOrgId },
  );

  // ---- Sync URL params → state on mount ----
  useEffect(() => {
    if (orgIdParam && organizations.length > 0 && !selectedOrg) {
      const match = organizations.find(o => o.organization_id === orgIdParam);
      if (match) setSelectedOrg(match);
    }
  }, [orgIdParam, organizations, selectedOrg]);

  useEffect(() => {
    if (userIdParam && orgUsers.length > 0 && !selectedUser) {
      const match = orgUsers.find(u => u.user_id === userIdParam);
      if (match) setSelectedUser(match);
    }
  }, [userIdParam, orgUsers, selectedUser]);

  // ---- Sync state → URL params ----
  const updateParams = useCallback(
    (orgId: string | null, userId: string | null) => {
      const params = new URLSearchParams();
      if (orgId) params.set('organizationId', orgId);
      if (userId) params.set('userId', userId);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const handleOrgChange = useCallback(
    (_e: unknown, value: AdminOrganizationResponse | null) => {
      setSelectedOrg(value);
      setSelectedUser(null);
      updateParams(value?.organization_id ?? null, null);
    },
    [updateParams],
  );

  const handleUserChange = useCallback(
    (_e: unknown, value: AdminOrganizationUserResponse | null) => {
      setSelectedUser(value);
      updateParams(effectiveOrgId || null, value?.user_id ?? null);
    },
    [updateParams, effectiveOrgId],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Science fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Shadow Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Test draft templates against real transcripts without polluting real data
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {/* Org picker */}
          <Autocomplete
            options={organizations}
            loading={orgsLoading}
            value={selectedOrg}
            onChange={handleOrgChange}
            inputValue={orgSearch}
            onInputChange={(_e, value) => setOrgSearch(value)}
            getOptionLabel={opt => opt.name || opt.organization_id}
            isOptionEqualToValue={(opt, val) => opt.organization_id === val.organization_id}
            sx={{ minWidth: 350 }}
            renderOption={(props, option) => (
              <li {...props} key={option.organization_id}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.organization_id} · {option.member_count ?? 0} members
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Search organizations..."
                size="small"
                required
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {orgsLoading && <CircularProgress size={18} />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          {/* User/provider picker (only when org is selected) */}
          <Autocomplete
            options={orgUsers}
            loading={orgDetailLoading}
            value={selectedUser}
            onChange={handleUserChange}
            getOptionLabel={opt => opt.name ?? opt.user_id}
            isOptionEqualToValue={(opt, val) => opt.user_id === val.user_id}
            disabled={!effectiveOrgId}
            sx={{ minWidth: 300 }}
            renderOption={(props, option) => (
              <li {...props} key={option.user_id}>
                <Box>
                  <Typography variant="body2">{option.name ?? 'Unnamed'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email ?? option.user_id}
                    {option.roles?.length ? ` · ${option.roles.join(', ')}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Provider (optional)"
                placeholder={effectiveOrgId ? 'Filter by provider...' : 'Select an org first'}
                size="small"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {orgDetailLoading && <CircularProgress size={18} />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Box>

        {!effectiveOrgId ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary" variant="h6">
              Select an organization to view shadow notes
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Failed to load shadow notes
            </Typography>
            <button
              onClick={() => void refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </Box>
        ) : (
          <DataTable<ShadowNoteResponse>
            title={`Shadow Notes (${data?.count ?? 0})`}
            columns={columns}
            data={(data?.shadow_notes as ShadowNoteResponse[]) || []}
            loading={isLoading}
            total={data?.count}
            onRowClick={row => {
              if (row.shadow_note_id) setViewingShadowNoteId(row.shadow_note_id);
            }}
            emptyMessage="No shadow notes found for this organization"
          />
        )}

        {/* Shadow note viewer dialog */}
        {viewingShadowNoteId && (
          <ShadowNoteViewerDialog
            open
            onClose={() => setViewingShadowNoteId(null)}
            shadowNoteId={viewingShadowNoteId}
            organizationId={effectiveOrgId}
          />
        )}
      </main>
    </div>
  );
}
